// SubtenantForm.tsx
import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  CircularProgress,
  Button,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { EntityPath } from '../../../types/plate';
import { Subtenant, subtenantApi as api } from '../../../api/subtenantApi';
import { useFetch } from '../../../hooks/useFetch4';
import { FormikRichTreeView } from '../../tree/FormikRichTreeView3';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { makeTreeItems } from '../../tree/tools';
import { useAuthStore } from '../../../store/useAuthStore3';

export interface SubtenantFormProps {
  subtenant: Subtenant | null;
  onSave: (values: Subtenant) => void;
  onCancel: () => void;
  entityPaths: EntityPath[];
}

const SubtenantForm: React.FC<SubtenantFormProps> = ({
  subtenant,
  onSave,
  onCancel,
  entityPaths,
}) => {
  const { t } = useTranslation();
  const title = subtenant ? t('SubtenantForm.edit') : t('SubtenantForm.new');
  const { customerId, subtenantId } = useAuthStore();

  // Загружаем связанные entity аналогично группам
  const fetchSubtenantToEntity = React.useCallback(
    () => api.getSubtenantToEntity(subtenant?.subtenant_id || -1),
    [subtenant?.subtenant_id]
  );
  
  const { data, error } = useFetch<number[]>(fetchSubtenantToEntity, []);
  const data_s = data.map(i => String(i));
  
  // const { data: isSubtenantActive } = useFetch<boolean>(api.getIsSubtenantActive, false, [subtenant?.subtenant_id]);
  // const [ isSubtenantActive, setIsSubtenantActive ] = useState(false);
  // useEffect(() => {
  //   if (subtenant) {
  //     const a = await useAuthStore.getState().getIsSubtenantActive(subtenant.subtenant_id);
  //     setIsSubtenantActive(a);
  //   }
  // }, [subtenant]);

  const apiCall = useAuthStore.getState().isSubtenantActive;

  const { data: isSubtenantActive } = useFetch<boolean>(
    apiCall,                       
    false,                         // defaultValue
    subtenant?.subtenant_id,       // initialPayload (передаем сам ID как аргумент для api)
    [subtenant?.subtenant_id]      // dependencies (следим за его изменением)
  );

  const initialValues: Subtenant = subtenant || {
    subtenant_id: -1,
    name: '',
    comment: '',
    max_parking_slots: 0,
    max_permits: 0,
    entity_ids: [],
    invitationEmail: '',
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(t('SubtenantForm.name_is_required')),
    max_parking_slots: Yup.number()
      .min(0, t('SubtenantForm.must_be_positive'))
      .required(t('SubtenantForm.required_field')),
    max_permits: Yup.number()
      .min(0, t('SubtenantForm.must_be_positive'))
      .required(t('SubtenantForm.required_field')),
    comment: Yup.string().nullable(),
  });

  if (customerId === -1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('Loading_form_data')}</Typography>
      </Box>
    );
  }

  const handleInvite = async (email: string | undefined) => {
    if (!email) {
      alert(t('SubtenantForm.enter_email_for_invitation'));
      return;
    }

    if (isSubtenantActive) {
      alert('Subtenant is active, invitation email cannot be sent.'); // TODO: i18n
      return;
    }

    await useAuthStore.getState().sendInvitationEmail(subtenant?.subtenant_id || -1, email);
    alert(t('SubtenantForm.invitation_sent_to') +' '+ email);
  };

  const treeItems: TreeViewBaseItem[] = makeTreeItems(entityPaths);

  console.log('[SubtenantForm] isSubtenantActive', isSubtenantActive);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        onSave(values);
      }}
      enableReinitialize
    >
      {(formikProps: FormikProps<Subtenant>) => {
        return (
          <Form>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              {/* Название субарендатора */}
              <Field name="name">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('SubtenantForm.name')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              <Stack direction="row" spacing={2}>
                {/* Лимит паркомест */}
                <Field name="max_parking_slots">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      type="number"
                      fullWidth
                      label={t('SubtenantForm.max_parking_slots')}
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>

                {/* Лимит пропусков */}
                <Field name="max_permits">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      type="number"
                      fullWidth
                      label={t('SubtenantForm.max_permits')}
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Stack>

              {/* Комментарий */}
              <Field name="comment">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={2}
                    label={t('SubtenantForm.comment')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              {/* Дерево локаций/entity, доступных субарендатору (только при редактировании) */}
              {/* {!!subtenant && ( */}
                <FormikRichTreeView
                  name="entity_ids"
                  label={t('SubtenantForm.link_to_entity')}
                  items={treeItems}
                  initialSelectedIds={data_s}
                  defaultExpandedItems={['1', '2']}
                  selectionPropagation={{ parents: true, descendants: true }}
                />
              {/* )} */}

              {subtenant && !isSubtenantActive && (
              <Field name="invitationEmail">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('SubtenantForm.invitationEmail')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
              )}
            </Stack>

            <Typography color='error' sx={{ mt: 1 }}>{error?.message}</Typography>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                onClick={() => handleInvite(formikProps.values.invitationEmail)}
                sx={{ mr: 1 }} 
                disabled={!subtenant || isSubtenantActive} 
                variant='outlined'
              >
                {t('SubtenantForm.SendInvitation')}
              </Button>
              <Button 
                onClick={onCancel} 
                sx={{ mr: 1 }} 
                disabled={formikProps.isSubmitting} 
                variant='outlined'
              >
                {t('OriginForm.Cancel')}
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
              >
                {formikProps.isSubmitting ? t('OriginForm.Saving') : t('GroupForm.Save')}
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SubtenantForm;
